import type { DictationIssue } from "shared";
import { isoToDateAndTime } from "utilz/helpers";

/** Same format as the home greeting's date. */
const RESET_DATE_FORMAT = "MMMM D, YYYY";

/**
 * What the technician reads for each way dictation can fail or end on its own.
 * The wording for minutes, unavailable, unreachable and stopped comes from the
 * backend handoff; provider error text is never shown.
 */
export const dictationIssueMessage = (
  issue: DictationIssue,
  timezone?: string,
): string => {
  switch (issue.kind) {
    case "permission_denied":
      return "Microphone access is blocked. Allow it in your browser's site settings to dictate.";
    case "no_microphone":
      return "No microphone found";
    case "disabled":
      return "Voice input isn't available for your account";
    case "minutes_exhausted":
      return issue.resetsAt
        ? `Voice minutes used up for this month; resets ${isoToDateAndTime({ isodate: issue.resetsAt, timezone, format: RESET_DATE_FORMAT })}`
        : "Voice minutes used up for this month";
    case "unavailable":
      return "Voice input is unavailable right now";
    case "unreachable":
      return "Couldn't reach the voice service";
    case "interrupted":
      return "Voice input stopped";
    case "no_speech":
      return "Voice input stopped due to inactivity";
    case "time_limit":
      return `Reached the ${Math.round(issue.maxSeconds / 60)}-minute limit`;
  }
};

/** `m:ss`, for the last-30-seconds countdown. */
export const formatDictationClock = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/** Shown in the dictation box while the no-speech auto-stop counts down. */
export const dictationSilenceWarning = (seconds: number): string =>
  `Stopping in ${seconds}s - keep talking to continue`;
